import React from 'react';
import { useNavigate } from 'react-router-dom';

import { RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import { canAccessComingSoonFeatures, hasAISpeedToLeadFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { COMING_SOON_FEATURE, COMING_SOON_FEATURES, COMING_SOON_ROUTE, ComingSoonFeatureKey } from './constants';

const Container = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
`;

const Card = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border-color: var(--line-gray-strong);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 7px;
  border-radius: 10px;

  &.blue {
    background-image: linear-gradient(to bottom, var(--blue-mist), var(--blue-sky));
  }
  &.pink {
    background-image: linear-gradient(to bottom, var(--pink-pale), var(--pink-soft));
  }
  &.gold {
    background-image: linear-gradient(to bottom, var(--gold-pale), var(--gold-soft));
  }

  img {
    width: 24px;
    height: 24px;
  }

  &.standalone {
    padding: 0;
  }

  &.standalone img {
    width: 38px;
    height: 38px;
  }
`;

const Title = styled.div`
  font-family: var(--font-bold);
`;

const Status = styled.div`
  font-size: 12px;
  color: var(--text-secondary);

  &.pilot {
    color: var(--positive-green);
  }
`;

const Description = styled.div`
  flex: 1;
  color: var(--text-secondary);
`;

const Action = styled.div`
  align-self: flex-start;
  color: var(--active-blue);
  font-family: var(--font-bold);

  &.pilot {
    padding: 5px 15px;
    border-radius: 4px;
    background-image: linear-gradient(90deg, var(--flamingo-pink), var(--competitor-purple));
    color: var(--white);
  }
`;

const ComingSoonFeatureCards = () => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const releasedComingSoonFeatures: ComingSoonFeatureKey[] = hasAISpeedToLeadFlag(currentUser)
    ? [COMING_SOON_FEATURE.SPEED_TO_LEAD]
    : [];
  const features = canAccessComingSoonFeatures(currentUser)
    ? COMING_SOON_FEATURES.filter((feature) => !releasedComingSoonFeatures.includes(feature.key))
    : [];

  if (!features.length) {
    return null;
  }

  return (
    <Container data-test-id="coming-soon-feature-cards">
      {features.map((feature) => (
        <Card
          key={feature.key}
          data-test-id={`coming-soon-feature-card-${feature.key}`}
          onClick={() => navigate(`${COMING_SOON_ROUTE}/${feature.key}`)}
        >
          <Header>
            <IconBox className={feature.iconClassName}>
              <img src={feature.iconImageSrc} alt="" />
            </IconBox>
            <div>
              <Title>{feature.label}</Title>
              <Status className={classNames({ pilot: feature.isPilot })}>
                {feature.isPilot ? 'Ready to pilot' : 'Coming soon'}
              </Status>
            </div>
          </Header>
          <Description>{feature.overviewDescription}</Description>
          <Action className={classNames({ pilot: feature.isPilot })}>
            {feature.isPilot ? 'Preview & apply to pilot' : 'Preview'} <RightOutlined />
          </Action>
        </Card>
      ))}
    </Container>
  );
};

export default ComingSoonFeatureCards;
